import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PageShell, PageHero } from "@/components/PageShell";
import { useCart } from "@/lib/cart";
import { formatPrice, PURCHASE_LABEL } from "@/lib/shop";
import { ProductImage } from "@/components/shop/ProductImage";
import { Loader2, Lock, ShieldCheck, ShoppingBag } from "lucide-react";

export const Route = createFileRoute("/shop/checkout")({
  component: Checkout,
  head: () => ({
    meta: [
      { title: "Secure checkout, N S Physiotherapy Shop" },
      { name: "description", content: "Complete your rehabilitation equipment order with secure online card and EFT payment." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type Config = { shipping_flat: number; free_shipping_threshold: number | null; payments_enabled: boolean };

function Checkout() {
  const { items, subtotal, setPurchaseType } = useCart();
  const navigate = useNavigate();
  const [config, setConfig] = useState<Config>({ shipping_flat: 95, free_shipping_threshold: null, payments_enabled: true });
  const [sameBilling, setSameBilling] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal: "",
    billing: "",
    notes: "",
  });

  useEffect(() => {
    fetch("/api/public/checkout")
      .then((r) => r.json())
      .then((d) => { if (d && typeof d.shipping_flat === "number") setConfig(d); })
      .catch(() => {});
  }, []);

  const shipping =
    subtotal <= 0
      ? 0
      : config.free_shipping_threshold != null && subtotal >= config.free_shipping_threshold
      ? 0
      : config.shipping_flat;
  const total = subtotal + shipping;

  const hasPhysio = items.some((l) => l.purchaseType === "physio");
  const hasStandard = items.some((l) => l.purchaseType === "standard");
  const orderPurchaseLabel = hasPhysio && hasStandard ? "Mixed, Standard and Physio Guided" : hasPhysio ? PURCHASE_LABEL.physio : PURCHASE_LABEL.standard;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer: { name: form.name, email: form.email, phone: form.phone },
          delivery: { address: form.address, city: form.city, postal_code: form.postal },
          billing_address: sameBilling ? null : form.billing || null,
          delivery_notes: form.notes || null,
          items: items.map((l) => ({
            productId: l.productId,
            purchaseType: l.purchaseType,
            qty: l.qty,
            options: l.options ?? {},
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.redirectUrl) {
        setError(data?.message || "We could not start your payment. Please try again.");
        setSubmitting(false);
        return;
      }
      // Cart stays intact until payment succeeds, so a failed payment can be retried.
      window.location.href = data.redirectUrl as string;
    } catch {
      setError("Network problem, please check your connection and try again.");
      setSubmitting(false);
    }
  }

  if (items.length === 0) {
    return (
      <PageShell>
        <section className="container-page py-16">
          <div className="mx-auto max-w-xl rounded-2xl border border-border bg-background p-8 text-center">
            <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground/50" />
            <h1 className="mt-4 font-display text-2xl font-bold text-primary">Your cart is empty</h1>
            <p className="mt-2 text-sm text-muted-foreground">Add products from the shop to continue to checkout.</p>
            <button onClick={() => navigate({ to: "/shop" })} className="btn-hero mt-6 inline-flex h-11 items-center rounded-full px-6 text-sm font-semibold">
              Browse products
            </button>
          </div>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHero eyebrow="Checkout" title="Complete your order" subtitle="Secure online payment by card or Instant EFT." />
      <section className="container-page py-10">
        <div className="grid gap-10 lg:grid-cols-[1fr_380px]">
          <form onSubmit={onSubmit} className="space-y-6">
            <fieldset className="rounded-2xl border border-border p-6">
              <legend className="px-2 font-display font-semibold text-primary">Your details</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                <Field label="Email address" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                <Field label="Mobile number" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
              </div>
            </fieldset>

            <fieldset className="rounded-2xl border border-border p-6">
              <legend className="px-2 font-display font-semibold text-primary">Delivery address</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field className="sm:col-span-2" label="Street address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} required />
                <Field label="City or town" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
                <Field label="Postal code" value={form.postal} onChange={(v) => setForm({ ...form, postal: v })} />
                <label className="sm:col-span-2 mt-1 flex items-start gap-2 text-sm text-muted-foreground">
                  <input type="checkbox" checked={sameBilling} onChange={(e) => setSameBilling(e.target.checked)} className="mt-0.5 h-4 w-4" />
                  <span>Billing address is the same as my delivery address</span>
                </label>
                {!sameBilling ? (
                  <Field className="sm:col-span-2" label="Billing address" value={form.billing} onChange={(v) => setForm({ ...form, billing: v })} />
                ) : null}
                <label className="sm:col-span-2 block">
                  <span className="text-xs font-medium text-primary/80">Delivery notes (optional)</span>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    maxLength={1000}
                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    placeholder="Complex name, gate code, preferred delivery time"
                  />
                </label>
              </div>
            </fieldset>

            <div className="rounded-2xl border border-border bg-secondary/40 p-5 text-sm text-muted-foreground">
              <div className="flex items-center gap-2 font-medium text-primary"><Lock className="h-4 w-4" /> Secure payment</div>
              <p className="mt-2">
                You will be redirected to iKhokha's secure payment page to pay by Visa, Mastercard, debit card, Instant EFT,
                Apple Pay or Google Pay where enabled. Card details are never stored on this website.
              </p>
            </div>

            {error ? (
              <p role="alert" className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">{error}</p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="btn-hero flex h-12 w-full items-center justify-center gap-2 rounded-full text-sm font-semibold disabled:opacity-70"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {submitting ? "Starting secure payment" : `Pay ${formatPrice(total)} securely`}
            </button>
          </form>

          <aside className="h-fit rounded-2xl border border-border bg-background p-6">
            <h2 className="font-display font-semibold text-primary">Order summary</h2>
            <p className="mt-1 text-xs text-muted-foreground">Purchase type: {orderPurchaseLabel}</p>
            <ul className="mt-4 divide-y divide-border">
              {items.map((l) => (
                <li key={l.key} className="flex items-center gap-3 py-3">
                  <ProductImage src={l.image} alt={l.name} className="h-12 w-12 rounded-lg bg-secondary p-1" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-primary">{l.name}</div>
                    <div className="text-xs text-muted-foreground">
                      Purchase Type: {PURCHASE_LABEL[l.purchaseType]} · Qty {l.qty}
                    </div>
                    {l.purchaseType === "physio" ? (
                      <button
                        type="button"
                        onClick={() => setPurchaseType(l.key, "standard", l.unitPrice)}
                        className="mt-1 text-[11px] text-primary underline underline-offset-2"
                      >
                        Change to Standard Purchase
                      </button>
                    ) : null}
                  </div>
                  <div className="text-sm font-semibold text-primary">{formatPrice(l.unitPrice * l.qty)}</div>
                </li>
              ))}
            </ul>
            <dl className="mt-4 space-y-1.5 border-t border-border pt-4 text-sm">
              <Row label="Subtotal" value={formatPrice(subtotal)} />
              <Row label="Delivery" value={shipping === 0 ? "Free" : formatPrice(shipping)} />
              <Row label="Total" value={formatPrice(total)} bold />
            </dl>
            <p className="mt-4 text-xs text-muted-foreground">
              Need help choosing? <Link to="/shop" className="text-primary underline underline-offset-2">Back to the shop</Link>
            </p>
          </aside>
        </div>
      </section>
    </PageShell>
  );
}

function Field({ label, value, onChange, type = "text", required, className = "" }: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; className?: string }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs font-medium text-primary/80">{label}{required && " *"}</span>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 h-11 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between ${bold ? "text-base font-semibold text-primary pt-2 border-t border-border" : "text-muted-foreground"}`}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
